<template>
    <div class="component-parametrage-table" :class="{ 'mobile': estMobile }">
        <h1>{{ titre }}</h1>

        <div v-if="chargement" class="etat">Chargement...</div>
        <div v-else-if="erreur" class="etat erreur">{{ erreur }}</div>

        <div v-else class="grille-conteneur">
            <table class="grille">
                <thead>
                    <tr>
                        <th v-for="col in colonnes" :key="col.cle">{{ col.libelle }}</th>
                        <th v-if="!readOnly" class="col-actions">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(ligne, i) in lignes" :key="i">
                        <td v-for="col in colonnes" :key="col.cle">
                            <input v-if="!readOnly"
                                   type="text"
                                   :data-cle="col.cle"
                                   v-model="ligne[col.cle]" />
                            <span v-else>{{ ligne[col.cle] }}</span>
                        </td>
                        <td v-if="!readOnly" class="col-actions">
                            <button class="btn-ligne btn-suppr" @click="supprimerLigne(i)">Supprimer</button>
                        </td>
                    </tr>
                    <tr v-if="!lignes.length">
                        <td :colspan="colonnes.length + (readOnly ? 0 : 1)" class="vide">Aucune donnee.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="barre-boutons">
            <button v-if="!readOnly && !chargement" @click="ajouterLigne">Ajouter une ligne</button>
            <span class="separateur"></span>
            <button v-if="!readOnly && !chargement" class="btn-primaire" @click="enregistrer">Enregistrer</button>
            <button v-if="!chargement" @click="exporter">Exporter CSV</button>
            <button @click="retour">{{ readOnly ? 'Fermer' : 'Annuler' }}</button>
        </div>
    </div>
</template>

<script>
    import { defineComponent } from 'vue';
    import TablesParam from '../script/TablesParam.js';
    import RequestManager from '../script/request.js';
    import { getDescripteurTable } from '../script/descripteursTables.js';

    export default defineComponent({
        props: {
            typpar: { type: [String, Number], default: null },
            // Mode panneau (integre dans l'ecran Parametres) : "Fermer" emet 'fermer'
            // au lieu de faire history.back() (pas de navigation de route).
            embarque: { type: Boolean, default: false },
        },
        emits: ['fermer'],
        data() {
            return {
                titre: "Parametrage",
                colonnes: [],
                lignes: [],
                readOnly: false,
                chargement: true,
                erreur: "",
            };
        },
        computed: {
            estMobile() {
                return RequestManager.estMobile;
            },
            typparCourant() {
                // Prop de route sinon query string (?typpar=N).
                return this.typpar !== null && this.typpar !== undefined && this.typpar !== ""
                    ? this.typpar
                    : this.$route.query.typpar;
            },
        },
        methods: {
            retour() {
                if (this.embarque) { this.$emit('fermer'); return; }
                history.back();
            },
            ajouterLigne() {
                const ligne = {};
                this.colonnes.forEach(c => { ligne[c.cle] = ""; });
                this.lignes.push(ligne);
            },
            supprimerLigne(i) {
                this.lignes.splice(i, 1);
            },
            // Deduit les colonnes de la 1re ligne quand le backend n'en fournit pas
            // (equivalent AutoGenerateColumns = True).
            colonnesAuto(lignes) {
                if (!lignes || !lignes.length) return [];
                return Object.keys(lignes[0]).map(k => ({ cle: k, libelle: k }));
            },
            async charger() {
                this.chargement = true;
                this.erreur = "";
                if (this.typparCourant === undefined || this.typparCourant === null || this.typparCourant === "") {
                    this.erreur = "Aucune table specifiee.";
                    this.chargement = false;
                    return;
                }
                const desc = getDescripteurTable(this.typparCourant);
                // Titre / colonnes explicites affichables avant la reponse serveur.
                if (desc) {
                    this.titre = desc.titre;
                    this.readOnly = !!desc.readOnly;
                }
                try {
                    const rep = await TablesParam.chargerTable(this.typparCourant);
                    if (!rep) { this.erreur = "Table introuvable."; return; }
                    // Lecture tolerante au casing : le vrai backend .NET renvoie du PascalCase,
                    // le contrat documente / les mocks sont en camelCase.
                    const repTitre = rep.titre ?? rep.Titre;
                    const repReadOnly = rep.readOnly ?? rep.ReadOnly;
                    const repLignes = rep.lignes ?? rep.Lignes;
                    const brutColonnes = rep.colonnes ?? rep.Colonnes;
                    // Normalise les clefs internes des colonnes en {cle, libelle} : le backend
                    // serialise les proprietes anonymes en PascalCase (Cle/Libelle) alors que le
                    // template lit cle/libelle. Sans ca, col.cle est undefined -> entetes ET
                    // cellules vides (ligne[undefined]) meme si les lignes sont bien chargees.
                    const repColonnes = Array.isArray(brutColonnes)
                        ? brutColonnes.map(c => {
                            const cle = c.cle ?? c.Cle;
                            return { cle, libelle: c.libelle ?? c.Libelle ?? cle };
                        })
                        : null;
                    this.titre = repTitre || this.titre;
                    this.readOnly = repReadOnly !== undefined ? !!repReadOnly : this.readOnly;
                    this.lignes = repLignes || [];
                    // Priorite : colonnes backend > colonnes descripteur > auto (1re ligne).
                    this.colonnes = (repColonnes && repColonnes.length)
                        ? repColonnes
                        : (desc && desc.colonnes ? desc.colonnes : this.colonnesAuto(this.lignes));
                } catch (e) {
                    this.erreur = RequestManager.decrireErreur(e);
                } finally {
                    this.chargement = false;
                }
            },
            async enregistrer() {
                try {
                    const r = await TablesParam.enregistrerTable(this.typparCourant, this.lignes);
                    const message = r && (r.message ?? r.Message);
                    const echec = r && (r.success === false || r.Success === false);
                    if (message) alert(message);
                    if (!echec) this.retour();
                } catch (e) {
                    alert(RequestManager.decrireErreur(e));
                }
            },
            exporter() {
                const desc = getDescripteurTable(this.typparCourant);
                const nom = desc ? desc.cle : "table";
                TablesParam.exporterCSV(nom, this.colonnes, this.lignes);
            },
        },
        mounted() {
            this.charger();
        },
    });
</script>

<style scoped>
    .component-parametrage-table {
        margin: 5px;
        display: flex;
        flex-direction: column;
        min-width: 0; /* autorise le retrecissement en contexte flex -> le tableau scrolle au lieu de deborder */
    }
    h1 {
        text-align: center;
        color: #01358D;
        font-size: 20px;
    }
    .etat { text-align: center; margin: 20px; }
    .etat.erreur { color: #b32020; }
    .grille-conteneur {
        background: white;
        overflow: auto;
        max-height: 70vh;
        width: 100%;
        max-width: 100%;
    }
    /* Modele de tableau force explicitement : un style global de l'app (table { display:flex })
       fuit et fait passer thead/tbody cote a cote (entetes a gauche des donnees). Nos regles
       scopees .grille ... l'emportent sur les selecteurs bare globaux et retablissent le tableau. */
    .grille {
        display: table;
        border-collapse: collapse;
        width: 100%;
    }
    .grille thead { display: table-header-group; }
    .grille tbody { display: table-row-group; }
    .grille tr { display: table-row; }
    .grille th, .grille td {
        display: table-cell;
        border: 1px solid #d0d7e2;
        padding: 4px 8px;
        text-align: left;
        vertical-align: middle;
    }
    .grille th {
        background: #004AAD;
        color: white;
        position: sticky;
        top: 0;
        white-space: nowrap;
    }
    /* Colonne Actions : dimensionnee au bouton (width:1% en layout auto = retrecir au contenu). */
    .grille th.col-actions, .grille td.col-actions {
        width: 1%;
        white-space: nowrap;
        text-align: center;
    }
    .grille input {
        width: 100%;
        min-width: 90px;
        box-sizing: border-box;
        padding: 3px;
        border: 1px solid #cfd8e3;
    }
    .col-actions { white-space: nowrap; }
    .vide { text-align: center; color: gray; }
    .btn-ligne {
        border: 1px solid #b9c4d4;
        border-radius: 4px;
        padding: 2px 6px;
        cursor: pointer;
    }
    .btn-ligne.btn-suppr { background: #fdecec; color: #b32020; border-color: #e6a3a3; }
    .barre-boutons {
        display: flex;
        gap: 8px;
        align-items: center;
        margin-top: 12px;
        padding: 8px;
        background: #f2f6fb;
        border-top: 1px solid #d0d7e2;
    }
    .barre-boutons .separateur { flex: 1; }
    .barre-boutons button {
        border: none;
        border-radius: 4px;
        padding: 8px 14px;
        background: #6b7d95;
        color: white;
        cursor: pointer;
    }
    .barre-boutons button.btn-primaire { background: #004AAD; }
    .barre-boutons button:hover { opacity: 0.9; }
    .component-parametrage-table.mobile .grille { min-width: 600px; }
</style>
